/* Site-wide auth context backed by Supabase.
 *
 * Holds the current session/user reactively (seeded from getSession(), kept in
 * sync via onAuthStateChange) and exposes thin wrappers over the supabase-js auth
 * methods used by the login screen and the account control. Login is entirely
 * client-side (email OTP + Google); the backend only verifies the resulting JWT. */
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import type {
  AuthError,
  AuthOtpResponse,
  AuthResponse,
  Session,
  User,
} from "@supabase/supabase-js";
import { supabase } from "../../lib/supabase";
import { fetchMe, type Me, type Role } from "../../api/me";

type AuthContextValue = {
  session: Session | null;
  user: User | null;
  loading: boolean;
  /* The caller's role + owned restaurants (GET /gw/me), the single source of
     truth the UI gates on. Null when logged out or while first resolving. */
  me: Me | null;
  /* Effective role — defaults to "user" when logged out or unresolved, so
     gating fails closed. */
  role: Role;
  /* True while /gw/me is in flight after a sign-in; gate admin views on this to
     avoid flashing "access denied" before the role is known. */
  roleLoading: boolean;
  isAdmin: boolean;
  /* May reach the admin panel at all (admin or owner). Regular users can't. */
  canAccessAdmin: boolean;
  /* Whether the caller may edit a given restaurant: admins can edit anything,
     owners only the ids they own, everyone else nothing. */
  canEditRestaurant: (restaurantId: string) => boolean;
  signInWithOtp: (email: string) => Promise<AuthOtpResponse>;
  verifyOtp: (email: string, code: string) => Promise<AuthResponse>;
  signInWithGoogle: () => Promise<{ error: AuthError | null }>;
  signOut: () => Promise<void>;
};

const AuthContext = createContext<AuthContextValue | null>(null);

type AuthProviderProps = { children: React.ReactNode };

export function AuthProvider({
  children,
}: AuthProviderProps): React.ReactElement {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [me, setMe] = useState<Me | null>(null);
  const [roleLoading, setRoleLoading] = useState(false);

  useEffect(() => {
    let active = true;

    // Seed from any persisted session, then let the listener take over.
    supabase.auth.getSession().then(({ data }) => {
      if (!active) return;
      setSession(data.session);
      setLoading(false);
    });

    const {
      data: { subscription },
    } = supabase.auth.onAuthStateChange((_event, next) => {
      setSession(next);
      setLoading(false);
    });

    return () => {
      active = false;
      subscription.unsubscribe();
    };
  }, []);

  // Load the caller's role once per signed-in user (GET /gw/me). Keyed on the
  // user id, not the whole session, so silent token refreshes don't refetch.
  // Clears on sign-out; on failure we fail closed (no elevated access).
  const userId = session?.user?.id ?? null;
  useEffect(() => {
    if (!userId) {
      setMe(null);
      setRoleLoading(false);
      return;
    }
    let active = true;
    setRoleLoading(true);
    fetchMe()
      .then((next) => active && setMe(next))
      .catch(() => active && setMe(null))
      .finally(() => active && setRoleLoading(false));
    return () => {
      active = false;
    };
  }, [userId]);

  const role: Role = me?.role ?? "user";
  const isAdmin = role === "admin";
  const canAccessAdmin = role === "admin" || role === "owner";

  const canEditRestaurant = useCallback(
    (restaurantId: string): boolean => {
      if (role === "admin") return true;
      if (role === "owner" && me) {
        return me.ownedRestaurantIds.includes(restaurantId);
      }
      return false;
    },
    [role, me],
  );

  const signInWithOtp = useCallback(
    (email: string) =>
      supabase.auth.signInWithOtp({
        email,
        options: { shouldCreateUser: true },
      }),
    [],
  );

  const verifyOtp = useCallback(
    (email: string, code: string) =>
      supabase.auth.verifyOtp({ email, token: code, type: "email" }),
    [],
  );

  const signInWithGoogle = useCallback(
    () =>
      supabase.auth.signInWithOAuth({
        provider: "google",
        options: { redirectTo: `${window.location.origin}/auth/callback` },
      }),
    [],
  );

  const signOut = useCallback(async () => {
    await supabase.auth.signOut();
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      user: session?.user ?? null,
      loading,
      me,
      role,
      roleLoading,
      isAdmin,
      canAccessAdmin,
      canEditRestaurant,
      signInWithOtp,
      verifyOtp,
      signInWithGoogle,
      signOut,
    }),
    [
      session,
      loading,
      me,
      role,
      roleLoading,
      isAdmin,
      canAccessAdmin,
      canEditRestaurant,
      signInWithOtp,
      verifyOtp,
      signInWithGoogle,
      signOut,
    ],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) {
    throw new Error("useAuth must be used inside <AuthProvider>");
  }
  return ctx;
}
